import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { orders as ordersApi } from '@/api/endpoints'
import { Alert, Field, HelpTip, Input, Modal, Select, Spinner } from '@/components/ui'
import { currencySymbol, formatDate, formatAmount } from '@/lib/utils'
import { PaymentType, type OrderDocument, type Payment } from '@/types/api'
import { DocumentLink, InvoiceButtons, ProviderFee, SendDocumentsButton, documentOf } from '@/features/pricing/OrderDocuments'
import { confirmRemovePayment } from '@/features/pricing/confirmRemovePayment'

const PAYMENT_TYPES = [PaymentType.Cash, PaymentType.Card, PaymentType.Transfer, PaymentType.Invoice, PaymentType.Other]

/**
 * Detail jednej objednávky (variabilného symbolu) — otvára sa z „Zoznamu
 * platieb“ kliknutím na riadok, alebo z „Pridať platbu“ po výbere klienta.
 *
 * Zámerne bez editácie položiek cenníka - to má na starosti
 * `PaymentDetailsModal` v okne rezervácie. Tu ide len o platby samotné:
 * čo prišlo, koľko zostáva, zapísať ďalšiu, prípadne zmazať omylom zapísanú.
 */
export default function OrderModal({
  orderRef,
  clientName,
  onClose,
}: {
  orderRef: number
  /** Meno na titulku, kým sa objednávka ešte len načítava. */
  clientName?: string | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const symbol = currencySymbol()
  const [error, setError] = useState<string | null>(null)

  const order = useQuery({
    queryKey: ['order', orderRef],
    queryFn: () => ordersApi.get(orderRef),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['order', orderRef] })
    void qc.invalidateQueries({ queryKey: ['payments'] })
    void qc.invalidateQueries({ queryKey: ['calendar'] })
  }

  const due = order.data ? Number.parseFloat(order.data.due) : 0
  const [amount, setAmount] = useState('')
  const [type, setType] = useState<number>(PaymentType.Cash)
  const [note, setNote] = useState('')
  const [ekasa, setEkasa] = useState('')

  useEffect(() => {
    setAmount(due > 0 ? due.toFixed(2) : '')
  }, [due])

  const pay = useMutation({
    mutationFn: () =>
      ordersApi.pay(orderRef, {
        amount: amount.replace(',', '.'),
        payment_type: type,
        note: note.trim(),
        ...(type === PaymentType.Cash && ekasa.trim() ? { ekasa_ref: ekasa.trim() } : {}),
      }),
    onSuccess: () => {
      setNote('')
      setEkasa('')
      setError(null)
      invalidate()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: (id: number) => ordersApi.removePayment(id),
    onSuccess: () => {
      setError(null)
      invalidate()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const typed = Number(amount.replace(',', '.'))
  const canPay = due > 0 && typed > 0 && typed <= due + 0.01

  const title = order.data?.client?.full_name || clientName || `#${orderRef}`

  return (
    <Modal open onClose={onClose} size="lg" title={`${title} — ${t('paymentsPage.orderTitle')}`}>
      <div className="space-y-3">
        {error ? <Alert variant="error">{error}</Alert> : null}

        {order.isLoading || !order.data ? (
          <div className="flex h-32 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : (
          <>
            <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                <div>
                  <div className="text-xs uppercase text-slate-500">
                    {t('reservation.paymentRef')}
                  </div>
                  <div className="font-mono text-base font-bold text-slate-800">{orderRef}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-slate-500">
                    {t('paymentsPage.lessons')}
                  </div>
                  <div className="text-sm text-slate-700">
                    {order.data.lessons.length}
                    {order.data.lessons.length ? (
                      <span className="ml-2 text-slate-500">
                        {formatDate(order.data.lessons[0].start)}
                        {order.data.lessons.length > 1
                          ? ` – ${formatDate(order.data.lessons[order.data.lessons.length - 1].start)}`
                          : ''}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="ml-auto flex items-center gap-5 text-right">
                  <Amount label={t('payments.charged')} value={order.data.total} symbol={symbol} />
                  <Amount
                    label={t('payments.received')}
                    value={order.data.paid}
                    symbol={symbol}
                    tone="text-green-700"
                  />
                  <Amount
                    label={t('payments.due')}
                    value={order.data.due}
                    symbol={symbol}
                    tone={due > 0 ? 'text-brand-600' : 'text-slate-500'}
                  />
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-md border border-slate-200">
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5">
                <span className="text-xs font-medium uppercase text-slate-500">{t('payments.receivedTitle')}</span>
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <InvoiceButtons order={order.data} />
                  <SendDocumentsButton order={order.data} />
                </div>
              </div>

              {order.data.payments.length ? (
                <table className="w-full border-collapse">
                  <tbody>
                    {order.data.payments.map((payment) => (
                      <PaymentRow
                        key={payment.id}
                        payment={payment}
                        symbol={symbol}
                        busy={remove.isPending}
                        document={documentOf(order.data, payment.id)}
                        onRemove={() => {
                          if (confirmRemovePayment(t, payment, symbol)) remove.mutate(payment.id)
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-4 text-center text-sm text-slate-400">
                  {t('payments.noPayments')}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 px-3 py-2.5">
                <Field label={t('payments.amount')} className="w-28">
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={amount}
                    disabled={due <= 0}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Field>
                <Field label={t('payments.method')} className="w-44">
                  <Select
                    value={type}
                    disabled={due <= 0}
                    onChange={(e) => setType(Number(e.target.value))}
                  >
                    {PAYMENT_TYPES.map((p) => (
                      <option key={p} value={p}>
                        {t(`reservation.payment.${p}`)}
                      </option>
                    ))}
                  </Select>
                </Field>
                {type === PaymentType.Cash ? (
                  <Field label={t('payments.ekasaRef')} className="w-44">
                    <Input
                      maxLength={64}
                      value={ekasa}
                      disabled={due <= 0}
                      placeholder={t('payments.ekasaPlaceholder')}
                      onChange={(e) => setEkasa(e.target.value)}
                    />
                  </Field>
                ) : null}
                <Field label={t('global.note')} className="min-w-[10rem] flex-1">
                  <Input
                    maxLength={150}
                    value={note}
                    disabled={due <= 0}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </Field>

                <button
                  type="button"
                  className="btn-primary whitespace-nowrap"
                  disabled={!canPay || pay.isPending}
                  onClick={() => pay.mutate()}
                >
                  {pay.isPending ? <Spinner /> : null}
                  {t('payments.record')}
                </button>

                {due <= 0 ? (
                  <p className="w-full text-xs text-slate-500">{t('payments.nothingDue')}</p>
                ) : (
                  <p className="w-full text-xs text-slate-500">{t('payments.partialAllowed')}</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

function Amount({
  label,
  value,
  symbol,
  tone = 'text-slate-800',
}: {
  label: string
  value: string
  symbol: string
  tone?: string
}) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className={`text-base font-bold ${tone}`}>
        {formatAmount(Number.parseFloat(value))} {symbol}
      </div>
    </div>
  )
}

function PaymentRow({
  payment,
  symbol,
  busy,
  onRemove,
  document,
}: {
  payment: Payment
  symbol: string
  busy: boolean
  onRemove: () => void
  /** Doklad k platbe (potvrdenie alebo VP). */
  document?: OrderDocument
}) {
  const { t } = useTranslation()

  return (
    <tr className="border-b border-slate-200 last:border-b-0 odd:bg-slate-50/60">
      <td className="w-32 px-3 py-2 text-sm text-slate-600">{formatDate(payment.created)}</td>
      <td className="px-3 py-2 text-sm">
        <span className="font-medium text-slate-700">
          {t(`reservation.payment.${payment.payment_type}`)}
        </span>
        {payment.note ? <span className="ml-2 text-slate-500">{payment.note}</span> : null}
        {document ? <DocumentLink document={document} /> : null}
        <ProviderFee payment={payment} />
        {payment.from_receipt && !document ? (
          <span className="ml-2 text-xs text-slate-400">{t('payments.fromReceipt')}</span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <span className="text-base font-bold text-green-700">
          {formatAmount(Number.parseFloat(payment.amount))}
        </span>
        <span className="ml-1.5 text-slate-600">{symbol}</span>
      </td>
      <td className="w-10 px-2 py-2 text-right">
        {payment.document ? (
          // Platba s dokladom sa nemaže - zámok vysvetlí, čo urobiť namiesto toho.
          <HelpTip label={t('payments.locked')} icon={<Lock size={14} />} align="right">
            {t(payment.document === 'invoice' ? 'payments.lockedInvoice' : 'payments.lockedReceipt')}
          </HelpTip>
        ) : (
          <button
            type="button"
            className="rounded p-1 text-slate-400 hover:bg-brand-50 hover:text-brand-600"
            title={t('global.delete')}
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 size={15} />
          </button>
        )}
      </td>
    </tr>
  )
}
